import { environment } from '$env/environment';
import { Injectable, Injector, OnDestroy } from '@angular/core';
import Echo from 'laravel-echo';
import { Subject } from 'rxjs';
import { first } from 'rxjs/operators';
import { AuthenticationService } from './auth.service';
import { SessionService } from './session.service';

declare global {
  interface Window {
    Pusher: any;
  }
}

export interface PrintInfoEvent {
  count_available_attempts: number;
  postal_delivery_order_id: number;
  qr_box_id: number;
  s10_code: string;
  check_id?: number;
}

export interface UserPush<T = any> {
  data: { data: T; status: string; type: string };
  message: null;
  subject: null;
}

@Injectable({
  providedIn: 'root',
})
export class SocketService implements OnDestroy {
  public events$ = new Subject<UserPush>();

  private _echo: Echo;

  private get echo(): Echo {
    return this._echo;
  }

  private set echo(v: Echo) {
    this._echo = v;
  }

  private get pusher(): any | null {
    try {
      return this.echo.connector.pusher;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  private authService: AuthenticationService;
  private sessionService: SessionService;

  constructor(private injector: Injector) {
    if (!environment.socket.enabled) {
      console.log('Socket disabled by env');
      return;
    }

    this.sessionService = this.injector.get(SessionService);
    this.authService = this.injector.get(AuthenticationService);
    this.authService.isAuthorized$.subscribe((e) => {
      if (e) {
        this.createSocket();
      } else {
        if (this.echo) {
          this.closeSocket(this.echo);
        }
      }
    });
  }

  public ngOnDestroy() {
    if (this.echo) {
      this.closeSocket(this.echo);
    }
  }

  public status(): string | null {
    try {
      return this.pusher.connection.state;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  private createSocket() {
    if (this.echo) {
      throw new Error('Socket instance already exists');
    }

    const config = environment.socket as any;

    this.echo = new Echo({
      broadcaster: 'pusher',
      key: 'key',
      wsHost: config.url,
      wsPort: config.port,
      wssPort: config.port,
      encrypted: config.encrypted,
      authEndpoint: `${config.encrypted ? 'https://' : 'http://'}${
        config.auth.url
      }:${config.auth.port}/broadcasting/auth`,
      auth: {
        headers: {
          Authorization: this.authService.getAccessTokenWithType(),
        },
      },
      enabledTransports: ['ws', 'wss'],
      disableStats: true,
    });

    this.pusher.connection.bind('state_change', this.log);
    this.pusher.connection.bind(
      'unavailable',
      this.connectionInterruptionHandler.bind(this)
    );

    this.sessionService.session$.pipe(first(Boolean)).subscribe((e: any) => {
      if (!e) {
        return;
      }
      this.joinUserChannel(e.id);
    });
  }

  private joinUserChannel(userID: number) {
    console.info('joinUserChannel constructor');

    this.echo
      .private(`user.${this.sessionService.session.id}`)
      .listen('UserPush', (e) => {
        console.log('%cSocket event', 'color: green', e);
        this.events$.next(e);
      });
  }

  private closeSocket(echo: Echo) {
    try {
      this.pusher.connection.unbind('state_change', this.log);
      this.pusher.connection.unbind(
        'disconnected',
        this.connectionInterruptionHandler
      );
      this.pusher.connection.unbind(
        'unavailable',
        this.connectionInterruptionHandler
      );
    } catch (error) {
      console.error('Failed to unbind socket events handler', error);
    }

    this.leaveAll(echo);

    if (echo && this.status() === 'connected') {
      setTimeout(() => {
        // Fixes "WebSocket is already in CLOSING or CLOSED state" exception
        echo.disconnect();
      }, 0);
    }

    this.echo = null;
  }

  private leaveAll(echo: Echo) {
    if (echo && echo.connector) {
      for (const key in echo.connector.channels) {
        if (echo.connector.channels.hasOwnProperty(key)) {
          echo.leave(key);
        }
      }
    }
  }

  private connectionInterruptionHandler() {
    const handleReconnect = () => {
      this.pusher.connection.unbind('connected', handleReconnect);
    };

    this.pusher.connection.bind('connected', handleReconnect);
  }

  private log(e: any) {
    console.log('Socket', e.current);
  }
}
